import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ActionForm } from '~/components/form/ActionForm';
import { LocationField } from '~/components/form/LocationField';
import { StatusToggle } from '~/components/form/StatusToggle';
import { TextField } from '~/components/form/TextField';
import { AttributesField } from '~/components/form/attributes/AttributesField';
import { Loading } from '~/components/ui/Loading';
import { SingleUploadField, UploadResult } from '~/components/upload2/Upload';
import {
  useCreateSpaceMutation,
  useUpdateSpaceMutation,
  useViewSpaceQuery,
} from '~/generated/graphql';
import { PromptEffect } from '../Prompt';
import { ErrorMessage } from '../ui/Error';
import { Label } from '../ui/Label';

type Props = {
  title: string;
  reload: () => void;
};

export const SpaceForm = ({ title, reload }: Props) => {
  const [, create] = useCreateSpaceMutation();
  const [, update] = useUpdateSpaceMutation();
  const navigate = useNavigate();
  const { id: spaceIdParam, spaceId: spaceIdAlt, siteId } = useParams();
  const spaceId = spaceIdParam ?? spaceIdAlt;
  const { t } = useTranslation(['translation', 'job']);

  const [result] = useViewSpaceQuery({
    pause: !spaceId,
    variables: { id: spaceId! },
  });
  const space = result.data?.spaces[0];
  if (result.fetching) {
    return <Loading />; // Primary loading check
  }
  if (spaceId && !space) {
    return (
      <div className='lg:w-rsb'>
        <ErrorMessage message='Space not found' />
      </div>
    );
  }

  const action = async (values: any) => {
    const { images: imageUploadResults, location, ...otherFormValues } = values;

    const imageIds = imageUploadResults.map((img: UploadResult) => img.id);

    const mutationInput = {
      ...otherFormValues,
      images: imageIds,
    };

    const res = spaceId
      ? await update({ id: spaceId, input: mutationInput })
      : await create({
          locationId: location
            ? `${location.type}:${location.id[0]}`
            : undefined,
          input: mutationInput,
        });

    if (res.error) {
      toast.error(res.error.message.replace('[GraphQL] ', ''));
      throw new Error();
    }
  };

  let formInitialValues: any;
  if (space) {
    formInitialValues = {
      status: space.status,
      name: space.name,
      attributes:
        space.attributes?.map((attr) => ({
          name: attr.name,
          value: attr.value,
        })) ?? [],
      images:
        space.image && space.images?.[0]
          ? [
              {
                id: space.images[0],
                preview: space.image,
                name:
                  space.images[0].substring(
                    space.images[0].lastIndexOf('/') + 1
                  ) || space.id + '_image.jpg',
                kind: 'image' as const,
              },
            ]
          : [],
    };
  } else {
    formInitialValues = {
      status: 'Active',
      name: '',
      location: {
        id: [],
        type: 'Site',
      },
      attributes: [],
      images: [],
    };
  }

  return (
    <ActionForm
      action={action}
      title={title}
      initialValues={formInitialValues}
      onCancel={() => {
        if (siteId) {
          navigate(`/sites/${siteId}`);
          return;
        }
        navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');
      }}
      onSuccess={({ data, status }) => {
        if (siteId) {
          navigate(`/sites/${siteId}`);
        } else {
          navigate(`/spaces/${data.id}`);
        }

        if (status === 201) {
          reload();
        }
      }}
    >
      <PromptEffect />
      <StatusToggle />
      <TextField name='name' label={t('spaceName')} className='mb-5' required />
      {space && (
        <>
          <Label>{t(space.location.__typename)}</Label>
          <div className='mb-5'>{space.location.name}</div>
        </>
      )}
      {!spaceId && <LocationField title={t('form.site')} required />}
      <AttributesField type='Space' />
      <SingleUploadField
        name='images'
        id='space-image'
        imagesOnly
        isMainUpload
      />
    </ActionForm>
  );
};
